import '../../App.css';
import TimeDelayDocument from '../TimeDelayDocument';
import TooltipSuggestionDocument from '../TooltipSuggestionDocument';
import TweakingTrailingWidgetDocument from './TweakingTrailingWidgetDocument';
import MainToolbar from '../HighlightMainToolbar';
import { defaultTheme, Provider, Flex, Button, AlertDialog, DialogTrigger, View } from '@adobe/react-spectrum';
import { useEffect, useState } from 'react';
import RedactDocument from '../RedactDocument';
import TweakingToolbar from './TweakingToolbar';
import TweakingRedactDocument from './TweakingRedactDocument';
import TweakingTimeDelayDocument from './TweakingTimeDelayDocument';

// Interface to customize the nudge parameters
function TweakingNudgingInterface({ workerId, experiment, passage, condition, pass, participantId, maxWordCount, constrainHighlights, callbacks }) {
  // TOOLBAR
  const [selectedTool, setSelectedTool] = useState("highlighter"); // current tool selected

  // WORD COUNT
  const [wordCount, setWordCount] = useState(0);
  const [progressWordCount, setProgressWordCount] = useState(0);
  const [shadowWordCount, setShadowWordCount] = useState(0);

  // HIGHLIGHTS
  const [highlights, setHighlights] = useState([]); // all of the raw selections
  const [allowedToHighlight, setAllowedToHighlight] = useState(true); // whether the user is allowed to highlight

  const [showError, setShowError] = useState(false); // whether the error message is shown


  // States for the toolbar

  // States for the redact, time delay, and trailing widget
  const [selectedLevel, setSelectedLevel] = useState('low');

  const [selectedRedact, setSelectedRedact] = useState(100); // the current redact amount selected (specific values for low, medium, and high)
  const [lowRedact, setLowRedact] = useState(100); // the redact amount for the low level
  const [medRedact, setMedRedact] = useState(100); // the redact amount for the medium level
  const [highRedact, setHighRedact] = useState(100); // the redact amount for the high level
  const [commentsRedact, setCommentsRedact] = useState(""); // the comments for the redact

  const [selectedTime, setSelectedTime] = useState(99); // the current time delay selected (specific values for low, medium, and high)
  const [lowTime, setLowTime] = useState(99); // the maximum time delay for the low level
  const [medTime, setMedTime] = useState(99); // the maximum time delay for the medium level
  const [highTime, setHighTime] = useState(99); // the maximum time delay for the high level
  const [commentsTime, setCommentsTime] = useState(""); // the comments for the time delay

  const [selectedSize, setSelectedSize] = useState(400); // the current trailing widget size selected (specific values for low, medium, and high)
  const [lowSize, setLowSize] = useState(400); // the size for the low level
  const [medSize, setMedSize] = useState(400); // the size for the medium level
  const [highSize, setHighSize] = useState(400); // the size for the high level
  const [commentsSize, setCommentsSize] = useState(""); // the comments for the trailing widget

  useEffect(() => {
    if (condition === 'time') {
      if (selectedLevel === 'low') {
        setSelectedTime(lowTime);
      } else if (selectedLevel === 'medium') {
        setSelectedTime(medTime);
      } else if (selectedLevel === 'high') {
        setSelectedTime(highTime);
      }
    }

    else if (condition === 'redact') {
      if (selectedLevel === 'low') {
        setSelectedRedact(lowRedact);
      } else if (selectedLevel === 'medium') {
        setSelectedRedact(medRedact);
      } else if (selectedLevel === 'high') {
        setSelectedRedact(highRedact);
      }
    }

    else if (condition === 'trailing') {
      if (selectedLevel === 'low') {
        setSelectedSize(lowSize);
      } else if (selectedLevel === 'medium') {
        setSelectedSize(medSize);
      } else if (selectedLevel === 'high') {
        setSelectedSize(highSize);
      }
    }

  }, [selectedLevel, lowTime, medTime, highTime, lowRedact, medRedact, highRedact, lowSize, medSize, highSize]);

  // reset the states when the condition changes
  useEffect(() => {
    if (pass < 2) {
      setSelectedLevel('low');
      setSelectedRedact(lowRedact);
      setSelectedTime(lowTime);
      setSelectedSize(lowSize);
    }
    else {
      if (selectedLevel === 'low') {
        setSelectedRedact(lowRedact);
        setSelectedTime(lowTime);
        setSelectedSize(lowSize);
      }
      else if (selectedLevel === 'medium') {
        setSelectedRedact(medRedact);
        setSelectedTime(medTime);
        setSelectedSize(medSize);
      }
      else if (selectedLevel === 'high') {
        setSelectedRedact(highRedact);
        setSelectedTime(highTime);
        setSelectedSize(highSize);
      }
    }
    setHighlights([]);
  }, [condition]);

  function countNumWords(newSelection) {
    // remove empty strings and punctuation
    var words = [];
    var punctuations = [".", ",", ":", "!", "?", "-", "(", ")", "[", "]", "{", "}", ";", "'", '"', "<", ">", "/", "\\"];
    newSelection.split(" ").forEach(element => {
      // check if the element is not empty and not a punctuation
      if (element !== "" && !punctuations.includes(element)) {
        words.push(element);
      }
    });

    return words.length;
  }

  return (
    <div style={{ backgroundColor: "#f8f8f8" }}>
      <Provider theme={defaultTheme} colorScheme='light'>
        <Flex direction="row" justifyContent={'start'} alignItems={"center"} width={"100vw"} height={"100vh"} gap={"size-200"}>
          <TweakingToolbar
            workerId={workerId}
            participantId={participantId}
            experiment={experiment}
            condition={condition}
            pass={pass}
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
            setHighlights={setHighlights}

            setLowRedact={setLowRedact}
            lowRedact={lowRedact}
            setMedRedact={setMedRedact}
            medRedact={medRedact}
            setHighRedact={setHighRedact}
            highRedact={highRedact}
            commentsRedact={commentsRedact}
            setCommentsRedact={setCommentsRedact}

            setLowTime={setLowTime}
            lowTime={lowTime}
            setMedTime={setMedTime}
            medTime={medTime}
            setHighTime={setHighTime}
            highTime={highTime}
            commentsTime={commentsTime}
            setCommentsTime={setCommentsTime}

            setLowSize={setLowSize}
            lowSize={lowSize}
            setMedSize={setMedSize}
            medSize={medSize}
            setHighSize={setHighSize}
            highSize={highSize}
            commentsSize={commentsSize}
            setCommentsSize={setCommentsSize}

            callbacks={callbacks}
          />

          <Flex direction="column" gap={"size-200"} height={"100vh"} justifyContent={'start'} alignItems={"center"} margin={'auto'}>
            {condition === 'redact' ?
              <>
                <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                <TweakingRedactDocument
                  redactAmount={selectedRedact / 100}
                  selectedLevel={selectedLevel}
                  countNumWords={countNumWords}
                  passage={passage}
                  allowedToHighlight={allowedToHighlight}
                  setAllowedToHighlight={setAllowedToHighlight}
                  highlights={highlights}
                  setHighlights={setHighlights}
                  selectedTool={selectedTool}
                  wordCount={wordCount}
                  setWordCount={setWordCount}
                  setProgressWordCount={setProgressWordCount}
                  setShadowWordCount={setShadowWordCount}
                  maxWordCount={maxWordCount}
                  targetWordCount={150} // nudging interaction techniques
                  showError={showError}
                  setShowError={setShowError}
                  constrainHighlights={constrainHighlights}
                  
                  workerId={workerId}
                  participantId={participantId}
                  condition={condition}
                  experiment={experiment}
                  pass={pass}
                />
              </> :

              condition === 'time' ?
                <>
                  <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                  <TweakingTimeDelayDocument
                    timeDelay={selectedTime}
                    selectedLevel={selectedLevel}
                    countNumWords={countNumWords}
                    passage={passage}
                    allowedToHighlight={allowedToHighlight}
                    setAllowedToHighlight={setAllowedToHighlight}
                    highlights={highlights}
                    setHighlights={setHighlights}
                    selectedTool={selectedTool}
                    wordCount={wordCount}
                    setWordCount={setWordCount}
                    setProgressWordCount={setProgressWordCount}
                    setShadowWordCount={setShadowWordCount}
                    maxWordCount={maxWordCount}
                    targetWordCount={150} // nudging interaction techniques
                    showError={showError}
                    setShowError={setShowError}
                    constrainHighlights={constrainHighlights}
                    
                    workerId={workerId}
                    participantId={participantId}
                    condition={condition}
                    experiment={experiment}
                    pass={pass}
                  />
                </> :

                condition === 'trailing' ?
                  <>
                    <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                    <TweakingTrailingWidgetDocument
                      mouseWidgetWidth={selectedSize}
                      selectedLevel={selectedLevel}
                      countNumWords={countNumWords}
                      passage={passage}
                      allowedToHighlight={allowedToHighlight}
                      setAllowedToHighlight={setAllowedToHighlight}
                      highlights={highlights}
                      setHighlights={setHighlights}
                      selectedTool={selectedTool}
                      wordCount={wordCount}
                      setWordCount={setWordCount}
                      setProgressWordCount={setProgressWordCount}
                      setShadowWordCount={setShadowWordCount}
                      maxWordCount={maxWordCount}
                      targetWordCount={150} // nudging interaction techniques
                      showError={showError}
                      setShowError={setShowError}
                      constrainHighlights={constrainHighlights}

                      workerId={workerId}
                      participantId={participantId}
                      condition={condition}
                      experiment={experiment}
                      pass={pass}
                    />
                  </>
                  : null
            }
          </Flex>

        </Flex>
      </Provider>
    </div>
  );
}

export default TweakingNudgingInterface;